import { c as _c } from "react/compiler-runtime";
import { Slider, ThumbCfg } from '../form/Slider';
import { DeltaValues } from 'shared/paths';
import { ForecastLogistic } from 'shared/forecast/forecastTypes';
import { Logistic } from 'shared/forecast/forecast';
import { ForecastQuestionAgg } from 'shared/forecast/forecast';
import { Bounds } from 'shared/forecast/bounds';
import { Rnge } from 'shared/range';
import { Fmt } from 'shared/forecast';
import { StyleProps } from 'shared/ui';
import { ForecastQuestionCfgAmount } from 'shared/forecast/forecast';
import { compactString } from 'shared/text';
import { DistributionChart, Dist, DistributionChartProps, DistLogistic } from 'shared/chart';
import { LabelForm, useElementSize } from 'shared/ui';
import { O } from 'ts-toolbelt';
import { match, P } from 'ts-pattern';
import { Badge, InputWrapper, InputWrapperProps, Popover, TextInput, useSafeMantineTheme } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { clamp, find, isDate, isEmpty, map, mapValues, pick, pipe, round } from 'remeda';
import { StringNumberInput } from 'components/form/StringNumberInput';
import { useKeyPress } from 'components/Keyboard';
import { DateInput, DatePicker, DatePickerProps } from '@mantine/dates';
import { DateTime as D } from 'luxon';
import { twMerge } from 'tailwind-merge';
import { ForecastAmountTailEdit, useHardBoundTails } from './ForecastAmountTail';
import { scaleLinear } from 'd3-scale';
import { useMergedRef } from '@mantine/hooks';
import { useDrag } from 'components/hooks';
const styleUnset = 'opacity-50';
type ThumbName = 'middle' | 'left' | 'right';
type LogisticDelta = DeltaValues<ForecastLogistic>;
export const ForecastLogisticEdit = ({
  currentForecast,
  dist,
  question: questionRaw,
  onDelta,
  margin,
  showGroup,
  className,
  style
}: {
  currentForecast: ForecastLogistic;
  dist: Dist.Distribution;
  question: Pick<ForecastQuestionCfgAmount, 'defaultBounds' | 'hardBounds' | 'forecastType' | 'unitLabel' | 'unitLabelLong' | 'numberFormat' | 'title' | 'amountType'>;
  questionAgg?: ForecastQuestionAgg;
  onDelta: (value: LogisticDelta) => void;
  margin: DistributionChartProps['margin'];
  showGroup?: boolean;
} & StyleProps) => {
  const question = questionRaw;
  const {
    colors
  } = useSafeMantineTheme();
  const {
    defaultBounds: questionBounds,
    unitLabel,
    unitLabelLong
  } = question;
  const displayForecast = Logistic.forecastWithDefaults(currentForecast, pick(question, ['defaultBounds', 'hardBounds']));
  const {
    bounds,
    boundsTuple
  } = Bounds.withDefaults(displayForecast.bounds, questionBounds);
  const thumbValue = cfgToThumb(displayForecast, Dist.pdfToDiscrete(DistLogistic.logisticToPdf({
    ...displayForecast,
    hardBounds: undefined,
    left: undefined,
    right: undefined
  })));
  const thumbValueRef = useRef<ThumbValues>(thumbValue);
  thumbValueRef.current = thumbValue;
  const distributionSet = currentForecast.location != null;
  const [focusedThumb, setFocusedThumb] = useState<ThumbName | undefined>();
  const middle = thumbValue.middle;
  const location = displayForecast.location;
  const locationFromMiddle = (newMiddle: number) => newMiddle + location - middle;
  const updateCfg = (delta: LogisticDelta) => onDelta(delta);
  const shiftPressedRef = useRef<boolean>(null);
  shiftPressedRef.current = useKeyPress('Shift');
  const roundAppropriately = (v: number) => {
    const getDecimalPlaces = (range: number) => {
      if (range <= 0) return 0;
      return Math.max(0, 2 + Math.floor(2 - Math.log10(range)));
    };
    const decimalPlaces = getDecimalPlaces(bounds.max - bounds.min);
    return round(v, decimalPlaces);
  };
  const getSliderChange = ({
    name,
    value,
    mode
  }: {
    name: ThumbName;
    value: number;
    hard?: boolean;
    mode: 'drag' | 'input' | 'arrows';
  }): LogisticDelta => {
    const roundIfNeeded = (v_0: number) => mode != 'drag' ? v_0 : roundAppropriately(v_0);
    const thumbOffset = specialXWidth('lr-thumb-offset', boundsTuple);
    let delta_1 = match(name).with('middle', () => ({
      location: locationFromMiddle(roundIfNeeded(value)),
      ...(currentForecast.scale ? {} : {
        scale: displayForecast.scale
      })
    })).with('left', 'right', n => {
      const middleMin = 0.1 * thumbOffset;
      const clampLr = (n_0: 'left' | 'right', v_1: number) => clamp(v_1, {
        [n_0 == 'left' ? 'max' : 'min']: middle + (n_0 == 'left' ? -thumbOffset - middleMin : +thumbOffset + middleMin)
      });
      const newValue = clampLr(n, value);
      const otherName = name == 'left' ? 'right' : 'left';
      const delta_0 = newValue - thumbValueRef.current[name];
      const getNewCfg = ({
        right,
        middle: middle_0,
        left
      }: Pick<Record<ThumbName, number>, 'middle' | 'left' | 'right'>) => {
        const {
          location: location_0,
          skew,
          scale
        } = DistLogistic.fitSkewLogistic({
          x10: left + thumbOffset,
          x50: middle_0,
          x90: right - thumbOffset
        });
        return {
          location: location_0,
          skew,
          scale
        };
      };
      const newCfg = getNewCfg({
        ...thumbValueRef.current,
        [name]: newValue,
        ...(shiftPressedRef.current ? {
          [otherName]: clampLr(otherName, thumbValueRef.current[otherName]! - delta_0)
        } : {})
      });
      if (Dist.isValid(dist)) return newCfg;
    }).otherwise(_ => ({})) as LogisticDelta;
    const newCfg_0 = {
      ...displayForecast,
      ...delta_1
    };
    const dxPan = match(cfgToThumb(newCfg_0, Dist.pdfToDiscrete(DistLogistic.logisticToPdf({
      ...newCfg_0,
      hardBounds: undefined
    })))).with({
      left: P.number.lt(bounds.min)
    }, ({
      left: left_0
    }) => left_0 - bounds.min).with({
      right: P.number.gt(bounds.max)
    }, ({
      right: right_0
    }) => right_0 - bounds.max).otherwise(() => undefined);
    if (dxPan) delta_1 = {
      ...delta_1,
      bounds: {
        min: bounds.min + dxPan,
        max: bounds.max + dxPan
      }
    };
    return delta_1;
  };
  const formats = Fmt.getOptions(question).absolute;
  const thumbLrCfg = {
    className: twMerge('!border-none !bg-none', !currentForecast.scale && styleUnset, shiftPressedRef.current && '!border-primary'),
    thumbSize: 20,
    shape: 'custom',
    hideOutOfRange: true
  } satisfies ThumbCfg;
  const thumbClass = currentForecast.location ? undefined : styleUnset;
  const lrPopoverProps = {
    amountType: question.amountType,
    unitLabel,
    unitLabelLong
  };
  const thumbs = mapValues({
    middle: {
      shouldClamp: true,
      showLabel: false,
      className: thumbClass,
      thumbSize: 30,
      children: __0 => <MiddleThumb value={roundAppropriately(middle)} amountType={question.amountType} unitLabel={unitLabel} unitLabelLong={unitLabelLong} onSubmit={v_2 => updateCfg({
        location: locationFromMiddle(v_2)
      })}>
            <div className='h-full w-full'></div>
          </MiddleThumb>
    } as ThumbCfg,
    left: {
      ...thumbLrCfg,
      children: __1 => {
        const value_0 = thumbValue.left + specialXWidth('lr-thumb-offset', boundsTuple);
        return <LeftRightThumb {...lrPopoverProps} side='left' value={roundAppropriately(value_0)} onSubmit={v_3 => {
          const offset = specialXWidth('lr-thumb-offset', boundsTuple);
          const thumbValue_0 = v_3 - offset;
          updateCfg(getSliderChange({
            name: 'left',
            value: thumbValue_0,
            mode: 'input'
          }));
        }}>
              <PickThumbSvg mode='left' />
            </LeftRightThumb>;
      }
    } as ThumbCfg,
    right: {
      ...thumbLrCfg,
      shape: 'custom',
      children: __2 => {
        const value_1 = thumbValue.right - specialXWidth('lr-thumb-offset', boundsTuple);
        return <LeftRightThumb {...lrPopoverProps} side='right' value={roundAppropriately(value_1)} onSubmit={v_4 => {
          const offset_0 = specialXWidth('lr-thumb-offset', boundsTuple);
          const thumbValue_1 = v_4 + offset_0;
          updateCfg(getSliderChange({
            name: 'right',
            value: thumbValue_1,
            mode: 'input'
          }));
        }}>
              <PickThumbSvg mode='right' />
            </LeftRightThumb>;
      }
    } as ThumbCfg
  }, (v_5, name_0) => ({
    ...v_5,
    onFocus: () => {
      setFocusedThumb(name_0);
    }
  }));
  const {
    ref: chartRef,
    width: chartWidth
  } = useElementSize();
  const xScale = chartWidth != null ? scaleLinear(Bounds.toTuple(currentForecast.bounds), [margin?.left ?? 0, chartWidth - (margin?.right ?? 0)]) : undefined;
  const tails = useHardBoundTails({
    forecast: displayForecast,
    question,
    onDelta
  });
  const {
    ref: moveRef
  } = useDrag({
    shouldClamp: false,
    onChange: ({
      delta: delta_2
    }) => {
      if (!xScale) return;
      const dx = delta_2.x * (currentForecast.bounds.max - currentForecast.bounds.min);
      onDelta({
        bounds: {
          min: bounds.min - dx,
          max: bounds.max - dx
        }
      });
    }
  });
  const mergedChartRef = useMergedRef(chartRef, moveRef);
  return <div className={twMerge('flex flex-col h-full', className)} style={style}>
      {distributionSet && Dist.isValid(dist) && xScale ? <div className='relative grow cursor-grab active:cursor-grabbing ' ref={mergedChartRef}>
          <DistributionChart margin={margin} dist={dist} fillParentHeight yAccessor={d => currentForecast.cumulative ? d.pAcc : d.p} chart={{
        bounds: boundsTuple,
        watermark: showGroup ? 'You' : undefined,
        watermarkProps: {
          style: {
            fill: 'var(--mantine-color-primary-filled)',
            fontSize: '1rem',
            opacity: 0.8
          }
        },
        primaryColor: 'var(--mantine-color-primary-filled)',
        formats
      }} xScale={xScale} tipOffsetY='2rem' // make this go above the menu
      />
          {chartWidth != null && tails.map(t => <ForecastAmountTailEdit key={t.side} {...t} xScale={xScale} />)}
        </div> : <HelpBackground />}
      <Slider className='mb-8 mt-2' style={{
      marginLeft: (margin?.left ?? 0) - 8,
      marginRight: (margin?.right ?? 0) - 8
    }} thumbSize={30} disabled={!Rnge.isAscending(boundsTuple)} thumbs={thumbs} value={thumbValue} min={bounds.min} max={bounds.max} onChange={({
      name: name_1,
      value: value_2,
      mode: mode_0
    }) => updateCfg(getSliderChange({
      name: name_1 as ThumbName,
      value: value_2,
      mode: mode_0
    }))} classNames={{
      bar: '!bg-gray-300',
      thumb: 'border-gray-400 !border-2 shadow-md'
    }} showLabelOnHover={false} marks={[{
      value: bounds.min
    }, {
      value: bounds.max
    }]} />
    </div>;
};
type ThumbControlProps = {
  value: number;
  amountType: 'number' | 'date';
  unitLabel: string;
  unitLabelLong?: string;
  onSubmit: (value: number) => void;
  children: React.ReactNode;
  percentileLabel?: string;
  withinPortal?: boolean;
};
const ThumbControl = t0 => {
  const $ = _c(37);
  const {
    value,
    amountType,
    unitLabel,
    unitLabelLong,
    onSubmit,
    children,
    percentileLabel,
    withinPortal
  } = t0;
  const [localValue, setLocalValue] = useState(value);
  const [opened, setOpened] = useState(false);
  let t1;
  let t2;
  if ($[0] !== value) {
    t1 = () => {
      setLocalValue(value);
    };
    t2 = [value];
    $[0] = value;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== localValue || $[4] !== onSubmit) {
    t3 = () => {
      onSubmit(localValue);
    };
    $[3] = localValue;
    $[4] = onSubmit;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleSubmit = t3;
  let t4;
  if ($[6] !== opened) {
    t4 = () => setOpened(!opened);
    $[6] = opened;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children || $[9] !== t4) {
    t5 = <Popover.Target><div className="h-full w-full" onClick={t4}>{children}</div></Popover.Target>;
    $[8] = children;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== amountType || $[12] !== handleSubmit || $[13] !== localValue || $[14] !== onSubmit || $[15] !== value) {
    let t7;
    if ($[17] !== handleSubmit || $[18] !== localValue) {
      t7 = () => <StringNumberInput value={localValue} onChange={setLocalValue} onKeyDownCapture={e_0 => {
        e_0.stopPropagation();
        if (e_0.key === "Enter") {
          handleSubmit();
          setOpened(false);
        }
      }} onBlur={() => {
        handleSubmit();
        setOpened(false);
      }} fmt={_temp2} autoFocus={true} selectOnFocus={true} />;
      $[17] = handleSubmit;
      $[18] = localValue;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== onSubmit || $[21] !== value) {
      t8 = () => <div onClick={_temp3}><DateInputMillis value={value} onChange={v_0 => {
          onSubmit(v_0);
        }} inline={true} autoFocus={true} /></div>;
      $[20] = onSubmit;
      $[21] = value;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    t6 = match(amountType).with("number", t7).with("date", t8).exhaustive();
    $[11] = amountType;
    $[12] = handleSubmit;
    $[13] = localValue;
    $[14] = onSubmit;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[23] !== unitLabel || $[24] !== unitLabelLong) {
    t7 = compactString(unitLabelLong) ?? unitLabel;
    $[23] = unitLabel;
    $[24] = unitLabelLong;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== percentileLabel || $[27] !== t7) {
    t8 = <LabelForm className="mt-1">{percentileLabel} {t7}</LabelForm>;
    $[26] = percentileLabel;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t6 || $[30] !== t8) {
    t9 = <Popover.Dropdown className="!p-2 !opacity-100" onMouseDownCapture={_temp}>{t6}{t8}</Popover.Dropdown>;
    $[29] = t6;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== opened || $[33] !== t5 || $[34] !== t9 || $[35] !== withinPortal) {
    t10 = <Popover withinPortal={withinPortal} position="bottom" withArrow={true} shadow="md" opened={opened} onChange={setOpened}>{t5}{t9}</Popover>;
    $[32] = opened;
    $[33] = t5;
    $[34] = t9;
    $[35] = withinPortal;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
};
const MiddleThumb = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ThumbControl {...props} percentileLabel="50th percentile" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const LeftRightThumb = t0 => {
  const $ = _c(6);
  let props;
  let side;
  if ($[0] !== t0) {
    ({
      side,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = side;
  } else {
    props = $[1];
    side = $[2];
  }
  const t1 = `${side === "left" ? "10" : "90"}th percentile`;
  let t2;
  if ($[3] !== props || $[4] !== t1) {
    t2 = <ThumbControl {...props} withinPortal={true} percentileLabel={t1} />;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const PickThumbSvg = t0 => {
  const $ = _c(5);
  const {
    mode
  } = t0;
  let t1;
  if ($[0] !== mode) {
    t1 = mode == "left" ? {
      transform: "scale(-1, 1) translate(-16, 0)"
    } : {};
    $[0] = mode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M0.5 9.00002C0.5 11.124 1.10235 13.1323 2.20554 14.5353C3.28665 15.9102 4.85679 16.7208 6.90014 16.5079C9.29771 15.78 11.3355 14.4574 12.7714 13.0215C14.2371 11.5558 15 10.0521 15 9.00002C15 7.92671 14.3395 6.41929 12.9515 4.97097C11.5844 3.54443 9.55277 2.22232 6.90896 1.49351C4.59804 1.27396 3.03516 2.09961 2.02597 3.45435C0.987995 4.84775 0.5 6.85296 0.5 9.00002Z" stroke="var(--_thumb-bd)" strokeWidth={1.5} fill="white" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <svg viewBox="0 0 16 18" fill="none"><g {...t1}>{t2}</g></svg>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const specialXWidth = (type: 'lr-thumb-offset' | 'min-thumb-width', bounds: [number, number]) => Rnge.width(bounds) * match(type).with('lr-thumb-offset', () => 0.008).with('min-thumb-width', () => 0.005).exhaustive();
const cfgToThumb = (cfg: O.Required<ForecastLogistic, 'location' | 'scale' | 'skew' | 'bounds'>, dist?: Dist.DiscreteDistribution) => {
  // this needs to be symmetrical to getSliderChange. i.e. thumb -> logistic -> thumb should be the same

  const {
    location
  } = cfg;
  const bounds = Bounds.toTuple(cfg.bounds);
  const thumbOffset = specialXWidth('lr-thumb-offset', bounds);
  const middle = Number(dist?.percentile50?.toFixed(10) ?? location);
  const left = (dist?.percentileX(0.1) ?? middle) - thumbOffset;
  const right = (dist?.percentileX(0.9) ?? middle) + thumbOffset;
  return {
    middle,
    left,
    right
  };
};
export type ThumbValues = ReturnType<typeof cfgToThumb>;
const dateFormats = ['d LLL yyyy',
// iso style
'yyyy-LL-dd', 'yyyy-LL-d',
// months iso
'yyyy-LL', 'yyyy-L',
// months human
'LLL yyyy', 'LL yyyy', 'yyyy LLL', 'yyyy LL',
//year
'yyyy'];
const {
  fromMillis,
  fromFormat
} = D;
const millisToText = (m: number) => fromMillis(m).toFormat('d LLL yyyy');
const textToDate = (t: string) => pipe(dateFormats, map(f => fromFormat(t.trim(), f)), find(d => d.isValid)) as any as D<true> | undefined;
export const DateInputMillis = t0 => {
  const $ = _c(43);
  let autoFocus;
  let inline;
  let inputProps;
  let onBlur;
  let onChange;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      inline,
      onBlur,
      autoFocus,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = autoFocus;
    $[2] = inline;
    $[3] = inputProps;
    $[4] = onBlur;
    $[5] = onChange;
    $[6] = value;
  } else {
    autoFocus = $[1];
    inline = $[2];
    inputProps = $[3];
    onBlur = $[4];
    onChange = $[5];
    value = $[6];
  }
  let t1;
  if ($[7] !== value) {
    t1 = millisToText(value);
    $[7] = value;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const [text, setText] = useState(t1);
  let t2;
  if ($[9] !== value) {
    t2 = fromMillis(value);
    $[9] = value;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const date = t2;
  let t3;
  if ($[11] !== text) {
    t3 = textToDate(text);
    $[11] = text;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const textDate = t3;
  let t4;
  if ($[13] !== date || $[14] !== textDate || $[15] !== value) {
    t4 = () => {
      if (!value) {
        return;
      }
      if (!textDate || !date.equals(textDate)) {
        setText(millisToText(value));
      }
    };
    $[13] = date;
    $[14] = textDate;
    $[15] = value;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== value) {
    t5 = [value];
    $[17] = value;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useEffect(t4, t5);
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      className: "!text-primary font-bold"
    };
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const yearMonthSelectedProps = t6;
  let t7;
  if ($[20] !== date) {
    t7 = date.toISODate() ?? undefined;
    $[20] = date;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== date) {
    t8 = date.toISODate();
    $[22] = date;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== date.year) {
    t10 = d => D.fromISO(d).year == date.year ? yearMonthSelectedProps : {};
    $[25] = date.year;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== date) {
    t11 = d_0 => D.fromISO(d_0).toFormat("yyyy-LL") == date.toFormat("yyyy-LL") ? yearMonthSelectedProps : {};
    $[27] = date;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t7 || $[32] !== t8) {
    t12 = {
      defaultDate: t7,
      value: t8,
      weekendDays: t9,
      getYearControlProps: t10,
      getMonthControlProps: t11
    };
    $[29] = t10;
    $[30] = t11;
    $[31] = t7;
    $[32] = t8;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const datPickerProps = t12 satisfies Partial<DatePickerProps>;
  let t13;
  if ($[34] !== autoFocus || $[35] !== datPickerProps || $[36] !== inline || $[37] !== inputProps || $[38] !== onBlur || $[39] !== onChange || $[40] !== text || $[41] !== textDate) {
    t13 = inline ? <InputWrapper className="space-y-2" {...inputProps}><TextInput value={text} onKeyDownCapture={_temp4} onChange={e_0 => {
        const v = e_0.target.value;
        if (v == text) {
          return;
        }
        setText(v);
        const d_1 = textToDate(v);
        if (d_1) {
          onChange(d_1.toMillis());
        }
      }} error={!isEmpty(text) && !textDate ? "valid formats: 12 Oct 2022,  2022-10-12,  2022-10, 2022" : undefined} autoFocus={autoFocus} onBlur={onBlur} /><DatePicker {...datPickerProps} onChange={v_0 => {
        if (v_0) {
          onChange(D.fromISO(v_0).toMillis());
        }
      }} /></InputWrapper> : <DateInput {...inputProps} {...datPickerProps} valueFormat="D MMM YYYY" onChange={s => {
      if (s) {
        onChange(D.fromISO(s).toMillis());
      }
    }} />;
    $[34] = autoFocus;
    $[35] = datPickerProps;
    $[36] = inline;
    $[37] = inputProps;
    $[38] = onBlur;
    $[39] = onChange;
    $[40] = text;
    $[41] = textDate;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
const Icon = {
  Arrow: {
    LeftRight: <svg width='54' height='23' viewBox='0 0 54 23' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M0.990453 11.03C0.454761 11.5876 0.472486 12.4739 1.03004 13.0095L10.116 21.7392C10.6735 22.2748 11.5598 22.2571 12.0955 21.6996C12.6312 21.142 12.6134 20.2558 12.0559 19.7201L3.9795 11.9604L11.7392 3.88404C12.2748 3.32648 12.2571 2.44023 11.6996 1.90453C11.142 1.36884 10.2558 1.38657 9.72006 1.94412L0.990453 11.03ZM53.0095 11.97C53.5452 11.4124 53.5275 10.5261 52.97 9.99045L43.884 1.26085C43.3265 0.725153 42.4402 0.742878 41.9045 1.30044C41.3688 1.85799 41.3866 2.74425 41.9441 3.27994L50.0205 11.0396L42.2608 19.116C41.7252 19.6735 41.7429 20.5598 42.3004 21.0955C42.858 21.6312 43.7442 21.6134 44.2799 21.0559L53.0095 11.97ZM2.02799 13.3997L52.028 12.3997L51.972 9.60028L1.97201 10.6003L2.02799 13.3997Z' fill='black' />
      </svg>,
    RightDown: <svg width='35' height='27' viewBox='0 0 35 27' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M1.70119 0.318994C0.889366 0.484015 0.365027 1.2759 0.530048 2.08773C0.69507 2.89955 1.48696 3.42389 2.29878 3.25887L1.70119 0.318994ZM24.5804 26.1405C25.2727 26.5954 26.2028 26.4029 26.6577 25.7105L34.0706 14.4279C34.5255 13.7355 34.333 12.8055 33.6406 12.3506C32.9483 11.8957 32.0182 12.0882 31.5634 12.7806L24.9741 22.8096L14.945 16.2203C14.2527 15.7654 13.3226 15.9579 12.8678 16.6503C12.4129 17.3426 12.6054 18.2727 13.2977 18.7276L24.5804 26.1405ZM1.99999 1.78893C2.29878 3.25887 2.29778 3.25908 2.29684 3.25927C2.29661 3.25931 2.29573 3.25949 2.29529 3.25958C2.29438 3.25976 2.29375 3.25988 2.29338 3.25996C2.29263 3.2601 2.29293 3.26004 2.29428 3.25979C2.29696 3.25927 2.30379 3.25799 2.31466 3.25605C2.33639 3.25218 2.37422 3.24571 2.42723 3.23766C2.53331 3.22155 2.69983 3.19915 2.91959 3.17842C3.35961 3.1369 4.00979 3.10239 4.81324 3.13755C6.42183 3.20795 8.62495 3.5565 10.9874 4.67061C15.6343 6.86206 21.2432 12.1853 23.9352 25.1909L26.8729 24.5828C24.032 10.8578 17.939 4.63207 12.267 1.95721C9.47003 0.638165 6.85955 0.224229 4.94439 0.140422C3.98596 0.0984813 3.19673 0.138947 2.6378 0.19168C2.35809 0.21807 2.13526 0.247608 1.97685 0.271661C1.89762 0.283692 1.83442 0.294364 1.78819 0.302606C1.76507 0.306727 1.74618 0.310242 1.73165 0.313017C1.72438 0.314405 1.7182 0.315608 1.71312 0.316609C1.71058 0.31711 1.70831 0.317561 1.70632 0.317959C1.70533 0.318158 1.70404 0.318416 1.70355 0.318516C1.70234 0.318761 1.70119 0.318994 1.99999 1.78893Z' fill='black' />
      </svg>,
    LeftDown: <svg width='33' height='25' viewBox='0 0 33 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M31.099 0.333756C31.9027 0.534762 32.3912 1.34921 32.1902 2.15289C31.9892 2.95656 31.1747 3.44511 30.3711 3.24411L31.099 0.333756ZM10.3998 23.9416C9.72913 24.4279 8.79122 24.2785 8.3049 23.6078L0.379842 12.6788C-0.106478 12.0081 0.0429581 11.0702 0.713617 10.5839C1.38428 10.0976 2.32219 10.247 2.80851 10.9177L9.85301 20.6324L19.5677 13.5879C20.2384 13.1016 21.1763 13.251 21.6626 13.9216C22.1489 14.5923 21.9995 15.5302 21.3288 16.0165L10.3998 23.9416ZM30.735 1.78893C30.3711 3.24411 30.372 3.24435 30.3729 3.24457C30.3731 3.24462 30.374 3.24483 30.3744 3.24493C30.3752 3.24514 30.3758 3.24529 30.3762 3.24537C30.3769 3.24554 30.3765 3.24546 30.3752 3.24515C30.3726 3.24452 30.366 3.24296 30.3555 3.2406C30.3345 3.23587 30.2982 3.22793 30.2473 3.21777C30.1455 3.19745 29.986 3.16834 29.7762 3.13836C29.3558 3.0783 28.7373 3.01531 27.9777 3.01131C26.4546 3.0033 24.3959 3.23234 22.2313 4.16995C18.0091 5.99882 12.951 10.7212 11.0005 22.9633L8.03792 22.4912C10.1087 9.49443 15.6585 3.74769 21.0389 1.41711C23.6756 0.27499 26.1669 0.0017396 27.9934 0.0113549C28.9086 0.0161724 29.6648 0.0919839 30.2005 0.168519C30.4687 0.206833 30.6826 0.245471 30.8346 0.275821C30.9106 0.291002 30.9713 0.304129 31.0157 0.314135C31.0378 0.319138 31.056 0.323363 31.0699 0.326677C31.0768 0.328334 31.0828 0.329763 31.0876 0.330949C31.09 0.331542 31.0922 0.332073 31.0941 0.332542C31.095 0.332776 31.0963 0.33308 31.0967 0.333197C31.0979 0.333484 31.099 0.333756 30.735 1.78893Z' fill='black' />
      </svg>
  }
};
const HelpBackground = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute bottom-9 w-[16em] left-[calc(50%-2em)] space-y-2"><div>change the 50th percentile</div><div className="opacity-50">{Icon.Arrow.LeftRight}</div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <br />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute bottom-1 @[500px]:bottom-2 right-[61%] text-right w-[16em] flex flex-col items-end gap-1"><div>change the 10th percentile{t1}<span className="text-sm">({" "}<Badge variant="light" color="gray">shift</Badge>{" "}to adjust symetrically)</span></div><div className="scale-x-50 scale-y-80 opacity-50">{Icon.Arrow.LeftRight}</div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="@container relative w-full flex justify-between opacity-40 font-semibold h-[10em] items-end">{t0}{t2}<div className="absolute bottom-1 @[500px]:bottom-2 left-[61%] w-[15em] flex flex-col items-start gap-1"><div>90th</div><div className="scale-x-50 scale-y-80 opacity-50">{Icon.Arrow.LeftRight}</div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(v) {
  return v.toString();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(e) {
  return e.stopPropagation();
}